// ---------------------------------------------------------------------------
// MULTI-FILIAIS: faixa "Nossas filiais" do rodapé.
//
// UM componente para as 5 variantes de rodapé (About1/rodape=1, About/2,
// About2/3, MegaFooter/4, EditorialFooter/5) — a lista de endereços é a mesma,
// só o fundo muda, e cada rodapé passa a cor que ele já calculou.
//
// PORTÃO (devolve null, zero markup, se qualquer um falhar):
//   • `modoPopup` (regioesConfig.modoEntrada === 'popup' + matriz), e
//   • pelo menos UMA região com endereço OU telefone/WhatsApp — sem dado de
//     contato a faixa seria uma lista de nomes soltos.
// Tenant sem regiões nunca chega aqui.
//
// O rodapé já mostrava só o telefone da filial ATIVA (aplicaRegiao); esta faixa
// é o oposto: mostra TODAS as filiais, que é o que o visitante procura no pé da
// página ("onde vocês ficam?").
// ---------------------------------------------------------------------------
import { FaEnvelope, FaMapMarkerAlt, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';
import { useRegiao } from '../../context/Info';
import { ensureReadableTextColor, isColorLight, MaskPhone } from '../../../utils/FunctionsGlobals';
import { ehModeloAurora } from '../aurora/aurora';
import styles from './FiliaisRodape.module.css';

const soDigitos = (valor?: string | null): string => String(valor || '').replace(/\D/g, '');

const FiliaisRodape: React.FC<{
    site: any;
    /** fundo da faixa — o rodapé passa o MESMO que já usa (default: site.cor3). */
    fundo?: string;
    /** cor do texto — default: derivada do fundo com contraste garantido. */
    cor?: string;
    /** 'atelier': linguagem do modelo (versalete no acento, nomes em serifa, linhas finas).
     *  'aurora': cartões de raio 20 com borda sutil e badge em pílula — também entra sozinha com
     *  `ehModeloAurora(site)` (topo 10), porque o AuroraFooter chama sem `variante`. */
    variante?: 'atelier' | 'aurora';
}> = ({ site, fundo, cor, variante }) => {
    const { regioes, modoPopup } = useRegiao();
    const aurora = variante === 'aurora' || ehModeloAurora(site);

    if (!modoPopup) return null;

    const comContato = regioes.filter(
        (r) => r.endereco || r.telefone || r.whatsapp
    );
    if (!comContato.length) return null;

    const corFundo = fundo || site?.cor3 || '#171717';
    const corTexto =
        cor ||
        ensureReadableTextColor(isColorLight(corFundo) ? site?.cor11 : site?.cor13, corFundo) ||
        '#ffffff';

    return (
        <section
            className={`${styles.faixa}${variante === 'atelier' ? ` ${styles.atelier}` : ''}${aurora ? ` ${styles.aurora}` : ''}`}
            style={{ background: corFundo, color: corTexto }}
            aria-labelledby="filiais-rodape-titulo"
            data-el="rodape-filiais"
        >
            <h2 className={styles.titulo} id="filiais-rodape-titulo">
                Nossas filiais
            </h2>
            <ul className={styles.grade}>
                {comContato.map((regiao) => {
                    const whats = soDigitos(regiao.whatsapp);
                    const tel = soDigitos(regiao.telefone);
                    return (
                        <li className={styles.cartao} key={regiao.slug}>
                            <span className={styles.nome}>
                                {regiao.nome}
                                {regiao.matriz ? <span className={styles.badge}>matriz</span> : null}
                            </span>

                            {regiao.endereco ? (
                                <p className={styles.endereco}>
                                    <FaMapMarkerAlt className={styles.icone} aria-hidden="true" />{' '}
                                    {regiao.endereco}
                                </p>
                            ) : null}

                            {whats ? (
                                <a
                                    className={styles.link}
                                    href={`https://api.whatsapp.com/send?phone=55${whats}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={`WhatsApp da filial ${regiao.nome}`}
                                >
                                    <FaWhatsapp className={styles.icone} aria-hidden="true" />
                                    {MaskPhone(String(regiao.whatsapp || ''))}
                                </a>
                            ) : null}

                            {tel && tel !== whats ? (
                                <a
                                    className={styles.link}
                                    href={`tel:+55${tel}`}
                                    aria-label={`Telefone da filial ${regiao.nome}`}
                                >
                                    <FaPhoneAlt className={styles.icone} aria-hidden="true" />
                                    {MaskPhone(String(regiao.telefone || ''))}
                                </a>
                            ) : null}

                            {regiao.email ? (
                                <a className={styles.link} href={`mailto:${regiao.email}`}>
                                    <FaEnvelope className={styles.icone} aria-hidden="true" />
                                    {regiao.email}
                                </a>
                            ) : null}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
};

export default FiliaisRodape;
